import type { LayerTree } from './layer';

/**
 * Scenes: the compositions a director prepares in advance and cuts between.
 *
 * A Show carries a preset; a Live starts from it and may diverge without
 * touching the Show - which is what `lives.scene_override` is for.
 */

export const TRANSITION_TYPES = [
  'cut',
  'fade',
  'wipe-left',
  'wipe-right',
  'wipe-up',
  'wipe-down',
  'slide-left',
  'slide-right',
  'slide-up',
  'slide-down',
] as const;

export type TransitionType = (typeof TRANSITION_TYPES)[number];

export interface Transition {
  type: TransitionType;
  /** Milliseconds. Ignored for `cut`, which is always instant. */
  duration: number;
}

export const DEFAULT_TRANSITION: Transition = { type: 'fade', duration: 500 };

export interface Scene {
  id: string;
  name: string;
  layers: LayerTree;
}

/**
 * What the studio holds. Only `programme` ever reaches a viewer.
 *
 * The programme is a **snapshot**, copied out of a scene when the director
 * takes it to air. Editing a scene - even the one currently on air - changes
 * nothing that is being broadcast until the next transition. The cost is that
 * fixing something live means transitioning again; the benefit is that no
 * stray drag can reach the audience, which is the trade a director expects.
 */
export interface SceneState {
  scenes: Scene[];
  /** The scene the programme snapshot was taken from. */
  activeSceneId: string | null;
  /** The scene being edited and shown in the preview panel. */
  previewSceneId: string | null;
  transition: Transition;
}

/** How the studio behaves, and how much of it is on screen. */
export const STUDIO_MODES = ['quick', 'studio'] as const;
export type StudioMode = (typeof STUDIO_MODES)[number];
